import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './FoodScan.css';
import '../pages/Landing.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ConsumerLabel =
  | 'raises concerns'
  | 'limit intake'
  | 'research ongoing'
  | 'no nutritional value'
  | 'generally ok';

interface Ingredient {
  position: number;
  raw_name: string;
  ai_description: string | null;
  nova_score: 1 | 2 | 3 | 4 | null;
  slug: string | null;
  library_name: string | null;
  consumer_label: ConsumerLabel | null;
  foodscan_summary: string | null;
  foodscan_flags: string[] | null;
  matched: boolean;
}

interface SuggestedAlternative {
  name: string;
  description: string;
}

interface FoodScan {
  nanoid: string;
  product_type: string | null;
  product_notes: string | null;
  product_image_url: string | null;
  product_image_status: 'pending' | 'generated' | 'failed';
  overall_summary: string | null;
  product_nova_score: 1 | 2 | 3 | 4 | null;
  suggested_alternatives: SuggestedAlternative[];
  status: 'pending' | 'complete' | 'failed';
  created_at: string;
}

// ---------------------------------------------------------------------------
// Display config
// ---------------------------------------------------------------------------

// Three verdict tiers used for the summary cards and section grouping.
type Tier = 'concern' | 'limit' | 'ok';

function tierForLabel(label: ConsumerLabel | null): Tier | null {
  switch (label) {
    case 'raises concerns':       return 'concern';
    case 'limit intake':          return 'limit';
    case 'research ongoing':      return 'limit';
    case 'no nutritional value':  return 'ok';
    case 'generally ok':          return 'ok';
    default:                      return null;
  }
}

const TIER_ACCENT: Record<Tier, string> = {
  concern: '#e57373',
  limit:   '#ffb74d',
  ok:      '#81c784',
};

const LABEL_BADGE: Record<ConsumerLabel, { bg: string; text: string }> = {
  'raises concerns':      { bg: '#fce4ec', text: '#c62828' },
  'limit intake':         { bg: '#fff3e0', text: '#e65100' },
  'research ongoing':     { bg: '#e3f2fd', text: '#1565c0' },
  'no nutritional value': { bg: '#eceff1', text: '#546e7a' },
  'generally ok':         { bg: '#e8f5e9', text: '#2e7d32' },
};

// Controlled flag vocabulary → pill color (see ingredients_schema.sql).
const FLAG_COLOR: Record<string, { bg: string; text: string }> = {
  'linked to health concerns': { bg: '#fce4ec', text: '#c62828' },
  'highly processed':          { bg: '#ede7f6', text: '#6a1b9a' },
  'no nutritional value':      { bg: '#e3f2fd', text: '#1565c0' },
  'artificial':                { bg: '#fce4ec', text: '#ad1457' },
  'high in saturated fat':     { bg: '#fff3e0', text: '#e65100' },
  'high in omega-6':           { bg: '#fff3e0', text: '#e65100' },
  'high glycemic impact':      { bg: '#fff3e0', text: '#e65100' },
  'allergen risk':             { bg: '#fce4ec', text: '#c62828' },
  'research ongoing':          { bg: '#e3f2fd', text: '#1565c0' },
  'gut health impact':         { bg: '#e0f2f1', text: '#00695c' },
};

const DEFAULT_FLAG_COLOR = { bg: '#eceff1', text: '#546e7a' };

// Sort priority within the "worth paying attention to" section.
const LABEL_ORDER: Record<ConsumerLabel, number> = {
  'raises concerns':      0,
  'limit intake':         1,
  'research ongoing':     2,
  'no nutritional value': 3,
  'generally ok':         4,
};

const NOVA_CONFIG: Record<number, { label: string; sublabel: string; bg: string; text: string }> = {
  1: { label: 'NOVA 1', sublabel: 'Unprocessed',          bg: '#e8f5e9', text: '#2e7d32' },
  2: { label: 'NOVA 2', sublabel: 'Minimally Processed',  bg: '#f1f8e9', text: '#558b2f' },
  3: { label: 'NOVA 3', sublabel: 'Processed',            bg: '#fff8e1', text: '#f57f17' },
  4: { label: 'NOVA 4', sublabel: 'Ultra-Processed',      bg: '#fce4ec', text: '#c62828' },
};

function NovaBadge({ score }: { score: number }) {
  const cfg = NOVA_CONFIG[score];
  if (!cfg) return null;
  return (
    <span className="fs-nova-badge" style={{ background: cfg.bg, color: cfg.text }}>
      <span className="fs-nova-badge-score">{cfg.label}</span>
      <span className="fs-nova-badge-sub">{cfg.sublabel}</span>
    </span>
  );
}

function FlagPills({ flags }: { flags: string[] }) {
  if (!flags.length) return null;
  return (
    <div className="fs-flag-pills">
      {flags.map((flag) => {
        const c = FLAG_COLOR[flag] ?? DEFAULT_FLAG_COLOR;
        return (
          <span key={flag} className="fs-flag-pill" style={{ background: c.bg, color: c.text }}>
            {flag}
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shell (shared layout)
// ---------------------------------------------------------------------------

function FoodScanShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="fs-page">
      <PublicHeader />
      {children}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Worth paying attention to" card
// ---------------------------------------------------------------------------

function AttentionCard({ ing }: { ing: Ingredient }) {
  const label = ing.consumer_label!;
  const tier = tierForLabel(label)!;
  const badge = LABEL_BADGE[label];
  // Always display the verbatim label text — never a library name that may add
  // qualifiers not present on the actual label.
  const name = ing.raw_name;

  return (
    <div className="fs-attn-card" style={{ borderLeftColor: TIER_ACCENT[tier] }}>
      <div className="fs-attn-card-head">
        <p className="fs-attn-name">{name}</p>
        <span className="fs-label-badge" style={{ background: badge.bg, color: badge.text }}>
          {label}
        </span>
      </div>

      {ing.foodscan_summary && <p className="fs-attn-summary">{ing.foodscan_summary}</p>}

      <FlagPills flags={ing.foodscan_flags ?? []} />

      <div className="fs-attn-foot">
        {ing.nova_score && <NovaBadge score={ing.nova_score} />}
        {ing.slug && (
          <Link to={`/ingredients/${ing.slug}`} className="fs-attn-details">
            Full details &rarr;
          </Link>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function FoodScanPage() {
  const { scan_id } = useParams<{ scan_id: string }>();
  const navigate = useNavigate();

  const [scan, setScan] = useState<FoodScan | null>(null);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'error' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [scan_id]);

  useEffect(() => {
    if (!scan_id) { setError('not_found'); setLoading(false); return; }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const res = await fetch(`/api/foodscan/${scan_id}`);
        if (cancelled) return;
        if (res.status === 404) { setError('not_found'); setLoading(false); return; }
        if (!res.ok) { setError('error'); setLoading(false); return; }
        const json = await res.json();
        setScan(json.data.scan);
        setIngredients(json.data.ingredients);
        setLoading(false);
      } catch {
        if (!cancelled) { setError('error'); setLoading(false); }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [scan_id]);

  // Poll for product image if still generating
  useEffect(() => {
    if (!scan || scan.product_image_status !== 'pending') return;
    const timer = setInterval(async () => {
      try {
        const res = await fetch(`/api/foodscan/${scan_id}`);
        if (!res.ok) return;
        const json = await res.json();
        const updated: FoodScan = json.data.scan;
        if (updated.product_image_status !== 'pending') {
          setScan(updated);
          clearInterval(timer);
        }
      } catch { /* silent */ }
    }, 6000);
    return () => clearInterval(timer);
  }, [scan, scan_id]);

  if (loading) {
    return (
      <FoodScanShell>
        <div className="fs-loading">
          <div className="fs-loading-spinner" />
          <p>Analyzing ingredients…</p>
        </div>
      </FoodScanShell>
    );
  }

  if (error || !scan) {
    return (
      <FoodScanShell>
        <div className="fs-not-found">
          <div className="fs-not-found-icon">🔍</div>
          <h2>{error === 'not_found' ? 'Scan not found' : 'Something went wrong'}</h2>
          <p>
            {error === 'not_found'
              ? "This food scan doesn't exist or may have expired."
              : 'We couldn\'t load this scan. Please try again.'}
          </p>
          <button className="btn hero-cta" onClick={() => navigate('/')}>Go Home</button>
        </div>
      </FoodScanShell>
    );
  }

  // ── Derive groupings ──
  const classified = ingredients.filter(i => i.consumer_label);
  const unmatched = ingredients.filter(i => !i.consumer_label);

  const concernCount = classified.filter(i => i.consumer_label === 'raises concerns').length;
  const limitCount   = classified.filter(i => i.consumer_label === 'limit intake' || i.consumer_label === 'research ongoing').length;
  const okCount      = classified.filter(i => i.consumer_label === 'no nutritional value' || i.consumer_label === 'generally ok').length;

  const attention = classified
    .filter(i => tierForLabel(i.consumer_label) === 'concern' || tierForLabel(i.consumer_label) === 'limit')
    .sort((a, b) => LABEL_ORDER[a.consumer_label!] - LABEL_ORDER[b.consumer_label!]);

  const okList = classified.filter(i => tierForLabel(i.consumer_label) === 'ok');

  const hasHighlyProcessed = classified.some(i => (i.foodscan_flags ?? []).includes('highly processed'));

  const productLabel = scan.product_type
    ? scan.product_type.charAt(0).toUpperCase() + scan.product_type.slice(1)
    : 'Packaged Food';

  return (
    <FoodScanShell>
      {/* ── Header ── */}
      <header className="fs-hero">
        <div className="landing-container fs-hero-inner">
          <div className="fs-hero-label">Foodscan results</div>
          <h1 className="fs-hero-title">{ingredients.length} ingredient{ingredients.length !== 1 ? 's' : ''} identified</h1>
          <p className="fs-hero-product">{productLabel}</p>
          {scan.product_notes && <p className="fs-hero-notes">{scan.product_notes}</p>}

          {/* Summary stat cards — by verdict tier (NOVA kept prominent) */}
          <div className="fs-summary-cards">
            <div className="fs-summary-card-stat" style={{ borderTopColor: TIER_ACCENT.concern }}>
              <span className="fs-summary-num" style={{ color: '#c62828' }}>{concernCount}</span>
              <span className="fs-summary-cap">Raises concerns</span>
            </div>
            <div className="fs-summary-card-stat" style={{ borderTopColor: TIER_ACCENT.limit }}>
              <span className="fs-summary-num" style={{ color: '#e65100' }}>{limitCount}</span>
              <span className="fs-summary-cap">Limit intake</span>
            </div>
            <div className="fs-summary-card-stat" style={{ borderTopColor: TIER_ACCENT.ok }}>
              <span className="fs-summary-num" style={{ color: '#2e7d32' }}>{okCount}</span>
              <span className="fs-summary-cap">Generally ok</span>
            </div>
          </div>

          {scan.product_nova_score && (
            <div className="fs-product-nova">
              <NovaBadge score={scan.product_nova_score} />
              <span className="fs-product-nova-label">Overall product processing level</span>
            </div>
          )}
        </div>
      </header>

      {/* ── AI disclaimer ── */}
      <div className="landing-container fs-disclaimer">
        AI-generated analysis. Information may contain errors — always verify with labels and a healthcare professional.
      </div>

      {/* ── Product packaging image ── */}
      <section className="landing-container fs-product-img-section">
        {scan.product_image_status === 'pending' && (
          <div className="fs-product-img-loading">
            <div className="fs-loading-spinner fs-loading-spinner--sm" />
            <span>Generating product illustration…</span>
          </div>
        )}
        {scan.product_image_status === 'generated' && scan.product_image_url && (
          <img
            src={scan.product_image_url}
            alt={`Generic ${productLabel} packaging illustration`}
            className="fs-product-img"
          />
        )}
      </section>

      <div className="landing-container fs-body">

        {/* ── Overall summary ── */}
        {scan.overall_summary && (
          <section className="fs-summary-overview">
            <h2 className="fs-section-title">Health overview</h2>
            <p className="fs-summary-text">{scan.overall_summary}</p>
          </section>
        )}

        {/* ── Empty state — only when nothing at all was identified ── */}
        {ingredients.length === 0 ? (
          <section className="fs-empty">
            <div className="fs-empty-icon">🔍</div>
            <p>
              We didn't recognize any ingredients from this scan. Try scanning the full
              ingredient list from the product's packaging.
            </p>
          </section>
        ) : (
          <>
            {/* ── Worth paying attention to ── */}
            {attention.length > 0 && (
              <section className="fs-attn-section">
                <h2 className="fs-section-title">Worth paying attention to</h2>
                <div className="fs-attn-grid">
                  {attention.map((ing) => (
                    <AttentionCard key={ing.position} ing={ing} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Generally ok + unmatched ── */}
            {(okList.length > 0 || unmatched.length > 0) && (
              <section className="fs-ok-section">
                <h2 className="fs-section-title">Generally ok</h2>
                <div className="fs-chip-list">
                  {okList.map((ing) => (
                    <span key={ing.position} className="fs-chip fs-chip--ok">{ing.raw_name}</span>
                  ))}
                  {unmatched.map((ing) => (
                    <span key={ing.position} className="fs-chip fs-chip--unknown" title="not in our database">
                      {ing.raw_name}
                    </span>
                  ))}
                </div>
                {unmatched.length > 0 && (
                  <p className="fs-chip-note">Gray chips aren't in our database yet.</p>
                )}
              </section>
            )}

            {/* ── mosaime callout ── */}
            {hasHighlyProcessed && (
              <section className="fs-callout">
                <p>
                  Several ingredients here may have started as natural sources but were refined
                  through industrial processes. mosaime explains not just what an ingredient is,
                  but how it got that way. Tap any ingredient above for the full picture.
                </p>
              </section>
            )}
          </>
        )}

        {/* ── Suggested alternatives ── */}
        {scan.suggested_alternatives && scan.suggested_alternatives.length > 0 && (
          <section className="fs-alternatives-section">
            <h2 className="fs-section-title">
              <span className="fs-alt-icon">✨</span> Better alternatives
            </h2>
            <div className="fs-alternatives-grid">
              {scan.suggested_alternatives.map((alt, i) => (
                <div key={i} className="fs-alt-card">
                  <p className="fs-alt-name">{alt.name}</p>
                  <p className="fs-alt-desc">{alt.description}</p>
                </div>
              ))}
            </div>
            <p className="fs-alt-label-note">
              Always check the label — even "healthier" alternatives can contain additives. The fewer ingredients, the better.
            </p>
          </section>
        )}

        {/* ── CTA ── */}
        <div className="fs-cta">
          <h3>Want to know what's really in your food?</h3>
          <p>mosaime helps you track what you eat and make better choices — delivered straight to your phone via SMS.</p>
          <Link to="/signup" className="btn hero-cta">Get Started Free</Link>
        </div>

      </div>
    </FoodScanShell>
  );
}

export default FoodScanPage;
